/**
 * Canvas 2D preview renderer for creature meshes — the creature lab's fallback while the WebGPU engine (F2) is not
 * available, and a QA tool that reads the exact art §3.2 buffers the engine receives.
 *
 * It skins the 24 B render mesh with the palette (2-bone LBS), shades each facet with the Cennini three bands (art
 * §4.1-A), evaluates the fur pattern per facet from partU/partV (art §2.6, simplified), draws the inverted hull's back
 * faces in ink inside the same painter's sort (art §4.3 → silhouette and interior outlines), and strokes `edgeMask`
 * creases. Presentation code (uses the DOM canvas); not for gameplay.
 */
import type { CreatureMeshData, Rig } from '../../shared';
import { m4LookAt, m4Mul, m4Ortho, m4Perspective } from '../../shared';
import { MATERIAL_MID, SHELL_STRIPE } from '../../content/creature/palettes';
import { deriveRamp, linearToSrgb, type LookColors, type Ramp, type RGB } from '../look';

interface Decoded {
  n: number; pos: Float32Array; u: Float32Array; v: Float32Array; bA: Uint8Array; bB: Uint8Array; w: Float32Array; mat: Uint8Array; reg: Uint8Array; edge: Uint8Array;
  hn: number; hpos: Float32Array; hnrm: Float32Array; hbA: Uint8Array; hbB: Uint8Array; hw: Float32Array; hlw: Float32Array; hidx: Uint16Array;
}
const cache = new WeakMap<CreatureMeshData, Decoded>();

function decode(mesh: CreatureMeshData): Decoded {
  const hit = cache.get(mesh);
  if (hit) return hit;
  const n = mesh.vertexCount;
  const dv = new DataView(mesh.vertices);
  const d: Decoded = {
    n, pos: new Float32Array(n * 3), u: new Float32Array(n), v: new Float32Array(n), bA: new Uint8Array(n), bB: new Uint8Array(n), w: new Float32Array(n),
    mat: new Uint8Array(n), reg: new Uint8Array(n), edge: new Uint8Array(n),
    hn: mesh.hull.vertexCount, hpos: new Float32Array(mesh.hull.vertexCount * 3), hnrm: new Float32Array(mesh.hull.vertexCount * 3),
    hbA: new Uint8Array(mesh.hull.vertexCount), hbB: new Uint8Array(mesh.hull.vertexCount), hw: new Float32Array(mesh.hull.vertexCount),
    hlw: new Float32Array(mesh.hull.vertexCount), hidx: mesh.hull.indices,
  };
  for (let i = 0; i < n; i++) {
    const o = i * 24;
    d.pos[i * 3] = dv.getFloat32(o, true); d.pos[i * 3 + 1] = dv.getFloat32(o + 4, true); d.pos[i * 3 + 2] = dv.getFloat32(o + 8, true);
    d.u[i] = (dv.getUint16(o + 12, true) / 65535) * 2; d.v[i] = dv.getUint16(o + 14, true) / 65535;
    d.bA[i] = dv.getUint8(o + 16); d.bB[i] = dv.getUint8(o + 17); d.w[i] = dv.getUint8(o + 18) / 255;
    d.mat[i] = dv.getUint8(o + 20); d.reg[i] = dv.getUint8(o + 21); d.edge[i] = dv.getUint8(o + 22);
  }
  const hv = new DataView(mesh.hull.vertices);
  for (let i = 0; i < d.hn; i++) {
    const o = i * 20;
    d.hpos[i * 3] = hv.getFloat32(o, true); d.hpos[i * 3 + 1] = hv.getFloat32(o + 4, true); d.hpos[i * 3 + 2] = hv.getFloat32(o + 8, true);
    d.hnrm[i * 3] = hv.getInt8(o + 12) / 127; d.hnrm[i * 3 + 1] = hv.getInt8(o + 13) / 127; d.hnrm[i * 3 + 2] = hv.getInt8(o + 14) / 127;
    d.hlw[i] = Math.max(0, hv.getInt8(o + 15) / 127) * 2;
    d.hbA[i] = hv.getUint8(o + 16); d.hbB[i] = hv.getUint8(o + 17); d.hw[i] = hv.getUint8(o + 18) / 255;
  }
  cache.set(mesh, d);
  return d;
}

export interface PreviewActor {
  mesh: CreatureMeshData;
  rig: Rig;
  colors: LookColors;
  /** boneCount × 16 skin matrices (world). */
  palette: Float32Array;
  /** 0 = sketch … 1 = painted (reveal). */
  paint?: number;
}

export interface PreviewCamera { eye: [number, number, number]; target: [number, number, number]; fovYDeg: number; orthoHalfH: number }

const toCss = (c: RGB, k = 1): string => {
  const s = linearToSrgb([c[0] * k, c[1] * k, c[2] * k]);
  return `rgb(${Math.round(Math.max(0, Math.min(1, s[0])) * 255)},${Math.round(Math.max(0, Math.min(1, s[1])) * 255)},${Math.round(Math.max(0, Math.min(1, s[2])) * 255)})`;
};

const hash3 = (x: number, y: number, z: number): number => {
  let h = (Math.imul(x | 0, 374761393) + Math.imul(y | 0, 668265263) + Math.imul(z | 0, 2147483647)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};
function vnoise(x: number, y: number, z: number): number {
  const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
  const xf = x - xi, yf = y - yi, zf = z - zi;
  const s = (t: number): number => t * t * (3 - 2 * t);
  const u = s(xf), v = s(yf), w = s(zf);
  const l = (a: number, b: number, t: number): number => a + (b - a) * t;
  return l(l(l(hash3(xi, yi, zi), hash3(xi + 1, yi, zi), u), l(hash3(xi, yi + 1, zi), hash3(xi + 1, yi + 1, zi), u), v),
    l(l(hash3(xi, yi, zi + 1), hash3(xi + 1, yi, zi + 1), u), l(hash3(xi, yi + 1, zi + 1), hash3(xi + 1, yi + 1, zi + 1), u), v), w);
}

const FIXED: Record<number, Ramp> = {};
function fixedRamp(mat: number): Ramp {
  if (!FIXED[mat]) FIXED[mat] = deriveRamp(MATERIAL_MID[mat] ?? '#F1E4C3', { hex: '#26306B', k: 0.22 });
  return FIXED[mat];
}
const INK: RGB = [0.0242, 0.0123, 0.0070]; // #2B1D14 linear
const SCLERA: RGB = [0.93, 0.88, 0.75];

/** Painter's-algorithm renderer. */
export class Preview2D {
  private readonly vp = new Float32Array(16);
  private readonly view = new Float32Array(16);
  private readonly proj = new Float32Array(16);
  private sx = new Float32Array(0); private sy = new Float32Array(0); private sz = new Float32Array(0); private wp = new Float32Array(0);
  keyDir: [number, number, number] = [-0.45, 0.75, 0.5];
  bandT1 = 0.44; bandT2 = 0.7;
  /** Ink line width in CSS px for the hull. */
  lineWidthPx = 2.0;

  constructor(readonly ctx: CanvasRenderingContext2D) {}

  setCamera(cam: PreviewCamera, wPx: number, hPx: number): void {
    m4LookAt(this.view, cam.eye, cam.target, [0, 1, 0]);
    if (cam.orthoHalfH > 0) { const a = wPx / hPx; m4Ortho(this.proj, -cam.orthoHalfH * a, cam.orthoHalfH * a, -cam.orthoHalfH, cam.orthoHalfH, 0.1, 100); }
    else m4Perspective(this.proj, (cam.fovYDeg * Math.PI) / 180, wPx / hPx, 0.1, 100);
    m4Mul(this.vp, this.proj, this.view);
    this.camEye = cam.eye; this.ortho = cam.orthoHalfH; this.camTarget = cam.target;
  }
  private camEye: [number, number, number] = [0, 0, 5];
  private camTarget: [number, number, number] = [0, 0, 0];
  private ortho = 0;

  /** Draws actors (already sorted back-to-front by the caller). `wPx/hPx` = canvas size in device px; `dpr` for line widths. */
  draw(actors: readonly PreviewActor[], wPx: number, hPx: number, dpr: number): void {
    const g = this.ctx;
    const L = norm(this.keyDir);
    const viewDir = norm([this.camTarget[0] - this.camEye[0], this.camTarget[1] - this.camEye[1], this.camTarget[2] - this.camEye[2]]);
    for (const a of actors) {
      const d = decode(a.mesh);
      const nT = d.n / 3;
      const total = d.n + d.hn;
      if (this.sx.length < total) { this.sx = new Float32Array(total); this.sy = new Float32Array(total); this.sz = new Float32Array(total); this.wp = new Float32Array(total * 3); }
      const P = a.palette;
      const skin = (px: number, py: number, pz: number, ba: number, bb: number, w: number, out: number): void => {
        const ia = ba * 16, ib = bb * 16;
        const ax = P[ia] * px + P[ia + 4] * py + P[ia + 8] * pz + P[ia + 12];
        const ay = P[ia + 1] * px + P[ia + 5] * py + P[ia + 9] * pz + P[ia + 13];
        const az = P[ia + 2] * px + P[ia + 6] * py + P[ia + 10] * pz + P[ia + 14];
        const bx = P[ib] * px + P[ib + 4] * py + P[ib + 8] * pz + P[ib + 12];
        const by = P[ib + 1] * px + P[ib + 5] * py + P[ib + 9] * pz + P[ib + 13];
        const bz = P[ib + 2] * px + P[ib + 6] * py + P[ib + 10] * pz + P[ib + 14];
        this.wp[out * 3] = bx + (ax - bx) * w; this.wp[out * 3 + 1] = by + (ay - by) * w; this.wp[out * 3 + 2] = bz + (az - bz) * w;
      };
      const project = (i: number): void => {
        const x = this.wp[i * 3], y = this.wp[i * 3 + 1], z = this.wp[i * 3 + 2];
        const m = this.vp;
        const cx = m[0] * x + m[4] * y + m[8] * z + m[12];
        const cy = m[1] * x + m[5] * y + m[9] * z + m[13];
        const cw = m[3] * x + m[7] * y + m[11] * z + m[15];
        const cz = m[2] * x + m[6] * y + m[10] * z + m[14];
        this.sx[i] = (cx / cw * 0.5 + 0.5) * wPx;
        this.sy[i] = (1 - (cy / cw * 0.5 + 0.5)) * hPx;
        this.sz[i] = cz / cw;
      };
      for (let i = 0; i < d.n; i++) { skin(d.pos[i * 3], d.pos[i * 3 + 1], d.pos[i * 3 + 2], d.bA[i], d.bB[i], d.w[i], i); project(i); }
      // Hull: skinned position + skinned normal × line width (world units per px at the vertex depth).
      const pxWorld = (wz: number): number => {
        if (this.ortho > 0) return (2 * this.ortho) / hPx;
        const dist = Math.max(0.1, Math.hypot(this.wp[wz * 3] - this.camEye[0], this.wp[wz * 3 + 1] - this.camEye[1], this.wp[wz * 3 + 2] - this.camEye[2]));
        return (2 * dist * Math.tan(0.5 * 0.42)) / hPx;
      };
      for (let i = 0; i < d.hn; i++) {
        const o = d.n + i;
        skin(d.hpos[i * 3], d.hpos[i * 3 + 1], d.hpos[i * 3 + 2], d.hbA[i], d.hbB[i], d.hw[i], o);
        // normal: transform by bone A rotation part
        const ia = d.hbA[i] * 16;
        const nx = d.hnrm[i * 3], ny = d.hnrm[i * 3 + 1], nz = d.hnrm[i * 3 + 2];
        const tx = P[ia] * nx + P[ia + 4] * ny + P[ia + 8] * nz, ty = P[ia + 1] * nx + P[ia + 5] * ny + P[ia + 9] * nz, tz = P[ia + 2] * nx + P[ia + 6] * ny + P[ia + 10] * nz;
        const l = Math.hypot(tx, ty, tz) || 1;
        const k = this.lineWidthPx * dpr * d.hlw[i] * pxWorld(o) * (0.9 + 0.35 * Math.max(0, -(tx * L[0] + ty * L[1] + tz * L[2]) / l));
        this.wp[o * 3] += (tx / l) * k; this.wp[o * 3 + 1] += (ty / l) * k; this.wp[o * 3 + 2] += (tz / l) * k;
        project(o);
      }
      // Collect triangles: body (front-facing) + hull back faces.
      const order: number[] = [];
      const depth: number[] = [];
      const facing = (i0: number, i1: number, i2: number): number => (this.sx[i1] - this.sx[i0]) * (this.sy[i2] - this.sy[i0]) - (this.sy[i1] - this.sy[i0]) * (this.sx[i2] - this.sx[i0]);
      for (let t = 0; t < nT; t++) {
        const i = t * 3;
        if (facing(i, i + 1, i + 2) >= 0) continue; // CCW in clip space = negative in y-down screen space
        order.push(t); depth.push((this.sz[i] + this.sz[i + 1] + this.sz[i + 2]) / 3);
      }
      const nH = d.hidx.length / 3;
      for (let t = 0; t < nH; t++) {
        const i0 = d.n + d.hidx[t * 3], i1 = d.n + d.hidx[t * 3 + 1], i2 = d.n + d.hidx[t * 3 + 2];
        if (facing(i0, i1, i2) < 0) continue; // inverted hull: keep the back faces
        order.push(-1 - t); depth.push((this.sz[i0] + this.sz[i1] + this.sz[i2]) / 3 + 0.00015);
      }
      const idx = order.map((_, k) => k).sort((x, y) => depth[y] - depth[x]);
      const paint = a.paint ?? 1;
      const inkCss = toCss(INK);
      g.lineJoin = 'round';
      for (const k of idx) {
        const t = order[k];
        if (t < 0) {
          const h = -1 - t;
          const i0 = d.n + d.hidx[h * 3], i1 = d.n + d.hidx[h * 3 + 1], i2 = d.n + d.hidx[h * 3 + 2];
          g.fillStyle = inkCss; g.strokeStyle = inkCss; g.lineWidth = 0.6;
          g.beginPath(); g.moveTo(this.sx[i0], this.sy[i0]); g.lineTo(this.sx[i1], this.sy[i1]); g.lineTo(this.sx[i2], this.sy[i2]); g.closePath(); g.fill(); g.stroke();
          continue;
        }
        const i = t * 3;
        // facet normal (world)
        const ax = this.wp[i * 3], ay = this.wp[i * 3 + 1], az = this.wp[i * 3 + 2];
        const ux = this.wp[(i + 1) * 3] - ax, uy = this.wp[(i + 1) * 3 + 1] - ay, uz = this.wp[(i + 1) * 3 + 2] - az;
        const vx = this.wp[(i + 2) * 3] - ax, vy = this.wp[(i + 2) * 3 + 1] - ay, vz = this.wp[(i + 2) * 3 + 2] - az;
        let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
        const nl = Math.hypot(nx, ny, nz) || 1; nx /= nl; ny /= nl; nz /= nl;
        if (nx * viewDir[0] + ny * viewDir[1] + nz * viewDir[2] > 0) { nx = -nx; ny = -ny; nz = -nz; }
        const mat = d.mat[i];
        const tt = (nx * L[0] + ny * L[1] + nz * L[2]) * 0.5 + 0.5 + (hash3(t, 7, 3) - 0.5) * 0.06;
        const band = tt < this.bandT1 ? 2 : tt < this.bandT2 ? 1 : 0;
        const col = this.colorOf(a.colors, d, i, mat, band);
        const css = paint < 1 ? toCss(mixRgb(SKETCH, col, paint)) : toCss(col);
        g.fillStyle = css; g.strokeStyle = css; g.lineWidth = 0.75;
        g.beginPath(); g.moveTo(this.sx[i], this.sy[i]); g.lineTo(this.sx[i + 1], this.sy[i + 1]); g.lineTo(this.sx[i + 2], this.sy[i + 2]); g.closePath(); g.fill(); g.stroke();
        const e = d.edge[i];
        if (e) {
          g.strokeStyle = inkCss; g.lineWidth = 1.2 * dpr;
          g.beginPath();
          if (e & 1) { g.moveTo(this.sx[i + 1], this.sy[i + 1]); g.lineTo(this.sx[i + 2], this.sy[i + 2]); }
          if (e & 2) { g.moveTo(this.sx[i + 2], this.sy[i + 2]); g.lineTo(this.sx[i], this.sy[i]); }
          if (e & 4) { g.moveTo(this.sx[i], this.sy[i]); g.lineTo(this.sx[i + 1], this.sy[i + 1]); }
          g.stroke();
        }
      }
    }
  }

  /** Material colour of a facet (art §4.1-C pattern, simplified to one evaluation per facet). */
  private colorOf(c: LookColors, d: Decoded, i: number, mat: number, band: number): RGB {
    const pick = (r: Ramp): RGB => (band === 0 ? r.light : band === 1 ? r.mid : r.shadow);
    switch (mat) {
      case 0: case 17: {
        const u = (d.u[i] + d.u[i + 1] + d.u[i + 2]) / 3;
        const v = (d.v[i] + d.v[i + 1] + d.v[i + 2]) / 3;
        const px = (d.pos[i * 3] + d.pos[i * 3 + 3] + d.pos[i * 3 + 6]) / 3, py = (d.pos[i * 3 + 1] + d.pos[i * 3 + 4] + d.pos[i * 3 + 7]) / 3,
          pz = (d.pos[i * 3 + 2] + d.pos[i * 3 + 5] + d.pos[i * 3 + 8]) / 3;
        const reg = d.reg[i];
        const p = c.pattern;
        let stripe = 0, belly = 0;
        const freq = p.freq || 9;
        switch (p.type) {
          case 1: case 2: { // mackerel / classic: ring stripes along the part spine (+ warp), dorsal line
            const s = u * freq * (reg === 0 || reg === 1 ? 0.6 : 1) + p.warp * (vnoise(px * 9, py * 9, pz * 9) * 2 - 1) * 1.6;
            const m = 0.5 + 0.5 * Math.sin(s * Math.PI * 2);
            stripe = m > p.threshold ? 1 : 0;
            if (reg === 4 && Math.abs(v - 0.75) < 0.06) stripe = 1;
            if ((reg === 1) || (reg === 4 && Math.abs(v - 0.25) < 0.1 && py < 0.6)) { stripe *= 0.0; belly = 0.85; }
            break;
          }
          case 3: { const cell = vnoise(px * freq, py * freq * 0.7, pz * freq); stripe = cell > 0.62 ? 1 : 0; break; }
          case 5: belly = (reg === 1 || ((reg === 4 || reg === 3) && Math.abs(v - 0.25) < p.bellyWidth + 0.06) || reg === 6 && py < 0.3) ? 1 : 0; break;
          case 6: stripe = reg === 2 || reg === 1 || reg === 9 || ((reg === 6 || reg === 8) && u > 0.06) ? 1 : 0; break;
          case 7: case 8: { const h = hash3(Math.floor(px * 5), Math.floor(py * 5), Math.floor(pz * 5)); stripe = h < 0.3 ? 1 : 0; belly = h > (p.type === 7 ? 0.8 : 1.1) ? 1 : 0; break; }
          case 14: { const cell = vnoise(px * 26, py * 26, pz * 26); stripe = cell > 0.7 ? 1 : 0; break; }
          default: break;
        }
        let col = pick(c.base);
        if (stripe) col = mixRgb(col, pick(c.stripe), Math.min(1, 0.55 + 0.45 * c.pattern.contrast));
        if (belly) col = mixRgb(col, pick(c.belly), belly);
        if (reg === 10 && mat === 0) col = pick(c.stripe); // hair, beards, manes use the hair (stripe) colour
        return col;
      }
      case 1: return pick(c.flesh);
      case 2: return SCLERA;
      case 3: return c.iris;
      case 4: return INK;
      case 5: case 6: case 7: case 8: return pick(c.dyes[mat - 5]);
      case 18: { const s = (d.u[i] * 18) % 1; return pick(fixedRamp(18)).map((x, k) => (s < 0.45 ? x * 0.62 + 0.0 * k : x)) as RGB; }
      default: return pick(fixedRamp(mat));
    }
  }
}

const SKETCH: RGB = [0.87, 0.78, 0.55];
const mixRgb = (a: RGB, b: RGB, t: number): RGB => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const norm = (v: readonly number[]): [number, number, number] => { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l]; };
export { SHELL_STRIPE };
